import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { mdiLoading } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { apiBaseUrl } from '../../api/client'

/**
 * "Add to the works plan" — the missing link between a recurrence ticket and a
 * budget line (spec 2026-08-24-01, lot 3).
 *
 * The ticket has proposed "investigate replacement" since spec 2026-07-18-01 and
 * there was nowhere to go with it: the manager agreed, then hand-created a
 * worksite, retyping what the ticket already knew.
 *
 * Deliberately confirmed rather than one-click: a budget line is a decision, and
 * the year is the part a human actually arbitrates. The backend proposes the
 * count-back's year when none is given, so leaving the field empty is a valid
 * answer — "take what the projection says".
 */

interface Props {
  orgCode: string
  issueUid: string
  assetUid: string
  /** Set once the ticket already produced a line, so the button stops offering. */
  existingWorksiteUid?: string | null
  existingPlanUid?: string | null
}

interface Result {
  worksite: { uid: string; name: string; year: number }
  plan_uid: string
  reused: boolean
}

export function AddToWorksPlanButton({
  orgCode,
  issueUid,
  assetUid,
  existingWorksiteUid,
  existingPlanUid,
}: Props) {
  const { t } = useTranslation('issues')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [year, setYear] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async (): Promise<Result> => {
      const token = localStorage.getItem('token')
      const res = await fetch(
        `${apiBaseUrl}/api/v1/organizations/${orgCode}/assets/${assetUid}/capex/worksite-from-ticket`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            issue_uid: issueUid,
            ...(year ? { year: Number(year) } : {}),
          }),
        },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error?.code ?? String(res.status))
      }
      return res.json()
    },
    onSuccess: (result) => {
      setOpen(false)
      queryClient.invalidateQueries({ queryKey: ['issue', orgCode, issueUid] })
      navigate({ to: '/$org/capex-plans/$planUid', params: { org: orgCode, planUid: result.plan_uid } })
    },
    onError: (err: Error) => {
      // The two failures a manager can act on get their own sentence; anything
      // else stays generic rather than leaking a code.
      setError(
        err.message === 'NO_DRAFT_PLAN' || err.message === 'YEAR_OUT_OF_RANGE'
          ? t(`works_plan.error_${err.message.toLowerCase()}`)
          : t('works_plan.error_generic'),
      )
    },
  })

  // Already done: point at the line instead of offering to create a second one.
  if (existingWorksiteUid && existingPlanUid) {
    return (
      <Button
        variant="outline"
        size="sm"
        data-testid="ticket-open-works-plan-btn"
        onClick={() =>
          navigate({ to: '/$org/capex-plans/$planUid', params: { org: orgCode, planUid: existingPlanUid } })
        }
      >
        {t('works_plan.open_existing')}
      </Button>
    )
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        data-testid="ticket-add-to-works-plan-btn"
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        {t('works_plan.cta')}
      </Button>

      <AlertDialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('works_plan.dialog_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('works_plan.dialog_description')}</AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2">
            <Label htmlFor="works-plan-year">{t('works_plan.year_label')}</Label>
            <Input
              id="works-plan-year"
              type="number"
              inputMode="numeric"
              value={year}
              placeholder={t('works_plan.year_placeholder')}
              onChange={(e) => setYear(e.target.value)}
              data-testid="works-plan-year-input"
            />
            <p className="text-xs text-muted-foreground">{t('works_plan.year_hint')}</p>
            {error && (
              <p className="text-sm text-destructive" data-testid="works-plan-error">
                {error}
              </p>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="works-plan-cancel">{t('works_plan.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending}
              onClick={(e) => {
                e.preventDefault()
                setError(null)
                mutation.mutate()
              }}
              data-testid="works-plan-confirm"
            >
              {mutation.isPending && <AppIcon path={mdiLoading} size="sm" className="mr-1 motion-safe:animate-spin" />}
              {t('works_plan.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
